import styled from 'foundations-theming/utils';
import { keyframes, css } from 'styled-components';
import { useEffect, useId, useRef, useState } from 'react';
import { TRACE_DURATION_MS } from './useHelpMeChooseAffordance';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const NEUTRAL_STROKE = '#1d1d1d';
const STROKE_WIDTH = 2.5;
// Normalized so dash math is independent of the pill's actual pixel length.
const NORMALIZED_PATH_LENGTH = 100;
function buildPillPath(width, height, strokeWidth) {
  const inset = strokeWidth / 2;
  const radius = (height - strokeWidth) / 2;
  const top = inset.toFixed(2);
  const bottom = (height - inset).toFixed(2);
  const left = (inset + radius).toFixed(2);
  const right = (width - inset - radius).toFixed(2);
  const r = radius.toFixed(2);
  return [`M ${left} ${top}`, `H ${right}`, `A ${r} ${r} 0 0 1 ${right} ${bottom}`, `H ${left}`, `A ${r} ${r} 0 0 1 ${left} ${top}`, 'Z'].join(' ');
}

// Draws the stroke around the pill, holds for ~5s, shrinks to thin at 90–93%, then ease-in fades out.
const drawStroke = keyframes(["0%{stroke-dashoffset:", ";opacity:0;stroke-width:", "px;}4%{opacity:1;}21%{stroke-dashoffset:0;opacity:1;}90%{stroke-dashoffset:0;opacity:1;stroke-width:", "px;}93%{stroke-dashoffset:0;opacity:1;stroke-width:1.5px;animation-timing-function:ease-in;}95%{stroke-dashoffset:0;opacity:0;stroke-width:1.5px;}to{stroke-dashoffset:0;opacity:0;stroke-width:1.5px;}"], NORMALIZED_PATH_LENGTH, STROKE_WIDTH, STROKE_WIDTH);

// idle: transparent link — tracing/settled: white pill with border (tertiary button look).
const PillWrapper = styled.div.withConfig({
  displayName: "GradientTraceBorder__PillWrapper"
})(["position:relative;display:inline-flex;align-items:center;border-radius:500px;padding-block:4px;padding-inline:16px;background-color:", ";transition:transform 0.25s cubic-bezier(0.34,1.56,0.64,1),background-color 0.3s ease;&:hover{transform:translateY(-3px);}", " @media (prefers-reduced-motion:reduce){transition:none;&:hover{transform:none;}}"], ({
  $phase
}) => $phase === 'idle' ? 'transparent' : '#fff', ({
  $phase
}) => $phase !== 'idle' && css(["button,button:hover{text-decoration:none;}"]));
const BorderSvg = styled.svg.withConfig({
  displayName: "GradientTraceBorder__BorderSvg"
})(["position:absolute;inset:0;width:100%;height:100%;overflow:visible;pointer-events:none;"]);

// Hidden in idle; always thin — TracePath covers it while thick, fades out revealing this border.
const UnderlayPath = styled.path.withConfig({
  displayName: "GradientTraceBorder__UnderlayPath"
})(["fill:none;stroke:", ";stroke-linejoin:round;stroke-width:1.5px;opacity:", ";transition:opacity 0.3s ease;"], NEUTRAL_STROKE, ({
  $phase
}) => $phase === 'idle' ? 0 : 1);

// Animated gradient stroke on top of the underlay — shrinks then fades out, leaving just the black border.
const TracePath = styled.path.withConfig({
  displayName: "GradientTraceBorder__TracePath"
})(["fill:none;stroke-linecap:round;stroke-linejoin:round;stroke-width:", "px;stroke-dasharray:", ";stroke-dashoffset:", ";opacity:", ";transition:opacity 0.25s ease-in;", " @media (prefers-reduced-motion:reduce){animation:none;stroke-dashoffset:0;opacity:", ";transition:none;}"], STROKE_WIDTH, NORMALIZED_PATH_LENGTH, ({
  $phase
}) => $phase === 'tracing' ? NORMALIZED_PATH_LENGTH : 0, ({
  $phase
}) => $phase === 'settled' ? 0 : 1, ({
  $phase
}) => $phase === 'tracing' && css(["animation:", " ", "ms ease-out forwards;"], drawStroke, TRACE_DURATION_MS), ({
  $phase
}) => $phase === 'settled' ? 0 : 1);
const GradientTraceBorder = ({
  phase,
  children
}) => {
  const wrapperRef = useRef(null);
  const [size, setSize] = useState({
    width: 0,
    height: 0
  });
  const gradientId = `hmc-border-${useId().replace(/:/g, '')}`;
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return undefined;
    const measure = () => {
      const {
        width,
        height
      } = el.getBoundingClientRect();
      setSize(prev => prev.width === width && prev.height === height ? prev : {
        width,
        height
      });
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  const pillPath = size.width > 0 && size.height > 0 ? buildPillPath(size.width, size.height, STROKE_WIDTH) : '';
  const cx = size.width / 2;
  const cy = size.height / 2;
  return /*#__PURE__*/_jsxs(PillWrapper, {
    ref: wrapperRef,
    $phase: phase,
    children: [pillPath && /*#__PURE__*/_jsxs(BorderSvg, {
      viewBox: `0 0 ${size.width} ${size.height}`,
      "aria-hidden": "true",
      focusable: "false",
      children: [/*#__PURE__*/_jsx("defs", {
        children: /*#__PURE__*/_jsxs("linearGradient", {
          id: gradientId,
          x1: "0",
          y1: "0",
          x2: size.width,
          y2: size.height,
          gradientUnits: "userSpaceOnUse",
          children: [/*#__PURE__*/_jsx("stop", {
            offset: "0%",
            stopColor: "#6e2ef4"
          }), /*#__PURE__*/_jsx("stop", {
            offset: "50%",
            stopColor: "#e0218a"
          }), /*#__PURE__*/_jsx("stop", {
            offset: "100%",
            stopColor: "#ff5c35"
          }), phase === 'tracing' && /*#__PURE__*/_jsx("animateTransform", {
            attributeName: "gradientTransform",
            type: "rotate",
            from: `0 ${cx} ${cy}`,
            to: `360 ${cx} ${cy}`,
            dur: "1200ms",
            repeatCount: "indefinite"
          })]
        })
      }), /*#__PURE__*/_jsx(UnderlayPath, {
        d: pillPath,
        $phase: phase
      }), phase !== 'idle' && /*#__PURE__*/_jsx(TracePath, {
        d: pillPath,
        pathLength: NORMALIZED_PATH_LENGTH,
        stroke: `url(#${gradientId})`,
        $phase: phase
      })]
    }), children]
  });
};
export default GradientTraceBorder;